import type { KeyboardEvent } from 'react';
import { CarouselArrow } from './CarouselArrow';
import { imageAltText, indexForKey, positionLabel, wrapIndex } from './carouselIndex';
import { ScanImageStrip } from './ScanImageStrip';
import { galleryStageClass } from './scanImageTabs';

const COUNTER_CLASS = {
    inline: 'bottom-2 px-2.5 py-0.5 text-[11px]',
    fullscreen: 'bottom-3 px-3 py-1 text-[12px]'
} as const;

interface StageImageProps {
    url: string | undefined;
    alt: string;
    onEnlarge?: () => void;
}

// Full screen has nothing left to enlarge, so the stage is plain there and the image fills it.
function StageImage({ url, alt, onEnlarge }: StageImageProps) {
    if (!onEnlarge) {
        return (
            <div className="grid h-full w-full place-items-center p-3">
                {url && (
                    <img
                        src={url}
                        alt={alt}
                        draggable={false}
                        className="h-full w-full object-contain"
                    />
                )}
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={onEnlarge}
            aria-label={`Show ${alt.toLowerCase()} full screen`}
            className="grid h-full w-full cursor-zoom-in place-items-center p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400"
        >
            {url && (
                <img
                    src={url}
                    alt={alt}
                    draggable={false}
                    className="max-h-full max-w-full object-contain"
                />
            )}
        </button>
    );
}

interface ScanImageCarouselProps {
    names: readonly string[];
    urls: readonly string[];
    index: number;
    label: string;
    imageNoun: string;
    fullscreen: boolean;
    onIndexChange: (index: number) => void;
    onEnlarge?: () => void;
}

export function ScanImageCarousel({
    names,
    urls,
    index,
    label,
    imageNoun,
    fullscreen,
    onIndexChange,
    onEnlarge
}: ScanImageCarouselProps) {
    const count = names.length;
    const arrowSize = fullscreen ? 'lg' : 'sm';

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const next = indexForKey(event.key, index, count);
        if (next === null) return;
        event.preventDefault();
        onIndexChange(next);
    };

    return (
        <div
            role="group"
            aria-roledescription="carousel"
            aria-label={label}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className={`flex flex-col rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 ${
                fullscreen ? 'min-h-0 flex-1 gap-3' : 'gap-2'
            }`}
        >
            <div className={`relative ${galleryStageClass(fullscreen)} overflow-hidden`}>
                <StageImage
                    url={urls[index]}
                    alt={imageAltText(imageNoun, index, count)}
                    onEnlarge={onEnlarge}
                />
                {count > 1 && (
                    <>
                        <CarouselArrow
                            direction="previous"
                            size={arrowSize}
                            onClick={() => onIndexChange(wrapIndex(index - 1, count))}
                        />
                        <CarouselArrow
                            direction="next"
                            size={arrowSize}
                            onClick={() => onIndexChange(wrapIndex(index + 1, count))}
                        />
                    </>
                )}
                <span
                    aria-live="polite"
                    className={`pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-full bg-gray-900/75 font-medium tabular-nums text-white ${COUNTER_CLASS[fullscreen ? 'fullscreen' : 'inline']}`}
                >
                    {positionLabel(index, count)}
                </span>
            </div>
            {count > 1 && (
                <ScanImageStrip
                    names={names}
                    urls={urls}
                    current={index}
                    imageNoun={imageNoun}
                    fullscreen={fullscreen}
                    onSelect={onIndexChange}
                />
            )}
        </div>
    );
}
